/**
 * A package on the quote, and the visits it put there.
 *
 * `docs/catalog-and-packages.md` §5.1. Somebody added "Porta pot rental ×4"
 * and got three visits — drop off, weekly service, pick up. Drawn as three
 * loose cards, the quote gives them three things to keep in step by hand; the
 * quantity, the start date and the term belong to the group, and changing one
 * of them is one edit that re-scales everything under it.
 *
 * The group is drawn in the accent tint so the boundary is visible without a
 * heading that says "package": the tint *is* the statement that these belong
 * together, and the visits inside keep their ordinary cards so nothing has to
 * learn a second way to read a visit.
 *
 * **Nothing here computes money.** `total`, `recurring`, `hours` and `cost`
 * all come off `QuotePackageInstanceView`, which the server rolled up from the
 * same visits drawn inside — so the group header and the cards under it cannot
 * disagree.
 */
import { type MouseEvent, type ReactNode } from 'react'
import { type QuotePackageInstanceView } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { formatDollars, formatNumber } from '../lib/format.js'
import { IconEdit, IconMoreVertical, IconPackage } from '../frame/icons.js'
import { QtyInput } from './quote-rows.js'
import { CollapseCaret } from '../builder/rows/caret.js'
import { MeasureChip } from '../builder/rows/measure-chip.js'

export interface PackageGroupProps {
  instance: QuotePackageInstanceView
  /** The visits this package produced, already rendered as ordinary cards. */
  children: ReactNode
  /** How many — only where the package asks for a quantity. */
  onQty: (qty: number) => void
  /**
   * The measure chip opens the Measure popover, as a service's does (Aaron,
   * review item 4.1): the number is looked up from the property, never typed
   * into a bare box on the header.
   */
  onEditMeasure: (event: MouseEvent<HTMLButtonElement>) => void
  /** The popover is open off this chip — the accent ring. */
  measureOpen?: boolean
  onEdit: (anchor: HTMLElement) => void
  onDetach: () => void
  onMenu: (at: { x: number; y: number }) => void
  /** Folded to its header — the caret, and Collapse all (step 21 item 4.4). */
  collapsed?: boolean
  onToggle?: () => void
  /**
   * A quote that is not a draft (walkthrough findings §4, folder 52): the
   * name, the quantity and the measure open nothing; Detach and ⋯ are absent.
   */
  readOnly?: boolean
}

export function PackageGroup({
  instance, children, onQty, onEditMeasure, measureOpen = false, onEdit, onDetach, onMenu,
  collapsed = false, onToggle, readOnly = false,
}: PackageGroupProps) {
  const margin = instance.total > 0
    ? Math.round(((instance.total - instance.cost) / instance.total) * 1000) / 10
    : 0

  return (
    <section
      className={cn('flex flex-col gap-2.5 rounded-[20px] border-[1.5px]',
        'border-accent-line bg-accent-soft p-3')}
    >
      <header className="flex items-center gap-3 px-0.5">
        {onToggle && <CollapseCaret collapsed={collapsed} onToggle={onToggle} label={instance.name} />}
        <span
          aria-hidden
          className={cn('ml-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-field',
            'bg-accent text-surface')}
        >
          <IconPackage size={19} />
        </span>

        <div className="flex min-w-0 flex-col gap-[3px]">
          <span className="flex items-center gap-2">
            <button
              type="button"
              onClick={(event) => onEdit(event.currentTarget)}
              disabled={readOnly}
              className="truncate text-[15px] font-bold text-text enabled:hover:text-accent-dark"
            >
              {instance.name}
            </button>
            <span className={cn('shrink-0 rounded-tag bg-accent px-1.5 py-px',
              'text-[10.5px] font-semibold uppercase tracking-[0.05em] text-surface')}>
              Package
            </span>
          </span>
          <span className="truncate text-body text-text-muted">
            {instance.visitIds.length}{' '}
            {instance.visitIds.length === 1 ? 'visit' : 'visits'} · starts{' '}
            {shortDate(instance.startsOn)}
          </span>
        </div>

        <div className="ml-auto flex items-center gap-[18px]">
          {/*
            * The numbers the whole group scales from. A package asks for a
            * quantity, a measurement, or both — and a package that asks for
            * neither is a fixed job, so it shows nothing rather than a box
            * that does not do anything.
            *
            * The quantity box is there only when the package asks *how
            * many*. A package priced by a measurement alone is added at a
            * quantity of 1 that nothing reads, and drawing it put `× [1] sf`
            * beside the measure — two boxes and no way to tell them apart
            * (Aaron, review item 4.1).
            */}
          {instance.asksQty && (
            <label className="inline-flex items-center gap-1.5 whitespace-nowrap text-body text-text-muted">
              ×
              <QtyInput
                value={instance.qty ?? 1}
                onCommit={onQty}
                disabled={readOnly}
                label={`How many ${instance.name}`}
                className="h-[30px] w-16 justify-center font-semibold"
              />
              {instance.qtyUnit}
            </label>
          )}

          {/*
            * The measure is the service row's chip, the same component:
            * `▦ 2,000 sf ✎`, or `Wall face —` once cleared, so a package
            * that asks for one can always be measured again. It opens the
            * Measure popover, which says what kind of measurement it is and
            * whether it follows the property or is this quote's own.
            */}
          {instance.asksMeasure && (
            <MeasureChip
              measure={instance.measure}
              unit={instance.measureUnit}
              measurementName={instance.measurementName}
              open={measureOpen}
              onClick={onEditMeasure}
              disabled={readOnly}
            />
          )}

          <span className="flex flex-col items-end gap-px">
            <b className="tnum text-[16px] font-bold text-text">
              {formatDollars(instance.total)}
            </b>
            {/*
              * What repeats, said separately from what totals. "+ $480 / week
              * while on rent" is the actual offer; folding it into the total
              * would be quoting a number of weeks nobody has agreed to.
              */}
            {instance.recurring.length > 0 && (
              <span className="whitespace-nowrap text-[11px] font-semibold text-accent-dark">
                {instance.recurring.map((amount) =>
                  `+ ${formatDollars(amount.amount)} ${amount.label}`).join(' · ')}
              </span>
            )}
            <span className="text-[11px] text-text-muted">
              {formatNumber(Math.round(instance.hours * 10) / 10)} h · margin{' '}
              <b>{margin}%</b>
            </span>
          </span>

          {!readOnly && <button
            type="button"
            onClick={onDetach}
            title="Keep these visits and stop them following the package"
            className={cn('shrink-0 rounded-button border border-accent-line bg-surface',
              'px-2.5 py-1 text-[12px] font-semibold text-text-body hover:text-accent-dark')}
          >
            Detach
          </button>}

          {!readOnly && <button
            type="button"
            aria-label={`${instance.name} options`}
            onClick={(event) => {
              const box = event.currentTarget.getBoundingClientRect()
              onMenu({ x: box.right, y: box.bottom })
            }}
            className="shrink-0 rounded-field p-1 text-text-faint hover:bg-surface hover:text-text"
          >
            <IconMoreVertical size={16} />
          </button>}
        </div>
      </header>

      {!collapsed && children}

      {!collapsed && <footer className="flex items-center gap-1.5 px-1 pb-0.5 text-[12px] text-text-muted">
        <IconEdit size={12} />
        Changing the quantity, the measure or the start re-scales every line
        nobody has typed over.
      </footer>}
    </section>
  )
}

/** "Oct 1" — the same short form the schedule label uses. */
function shortDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}
